import { type ReactNode } from 'react';
import { Pressable, View } from 'react-native';

import { Text } from '@/components/ui';
import type { ContextSet } from '@/lib/chat/types';

type ChatContextBinProps = {
  contextSet: ContextSet;
  expanded: boolean;
  instructions: string | null;
  onToggleExpanded: () => void;
  onContextSetChange: (next: ContextSet) => void;
  onInstructionsChange: (next: string) => void;
  usageLine?: ReactNode;
};

function discoverySummary(contextSet: ContextSet): string {
  if (contextSet.discovery === 'off') {
    return 'Discovery: off';
  }
  const scopeCount = contextSet.scopeRoots?.length ?? 0;
  if (contextSet.discovery === 'scoped' && scopeCount > 0) {
    return `Discovery: ${scopeCount} ${scopeCount === 1 ? 'area' : 'areas'}`;
  }
  return 'Discovery: whole workspace';
}

export function ChatContextBin({
  contextSet,
  expanded,
  instructions,
  onToggleExpanded,
  onInstructionsChange,
  usageLine,
}: ChatContextBinProps) {
  const pinCount = contextSet.pins.length;

  return (
    <View className="chat-context-bin">
      <Pressable
        className="chat-context-bin-header flex-row items-center gap-1"
        onPress={onToggleExpanded}
      >
        <Text variant="caption" className="shrink-0 font-medium">
          Context · {pinCount} {pinCount === 1 ? 'pin' : 'pins'}
        </Text>
        <Text variant="caption" className="shrink-0 text-muted">
          {expanded ? '▾' : '▸'}
        </Text>
        {!expanded && usageLine ? (
          <View className="min-w-0 flex-1 flex-row items-center overflow-hidden">{usageLine}</View>
        ) : null}
      </Pressable>

      {expanded ? (
        <View className="chat-context-bin-body">
          {pinCount === 0 ? (
            <Text variant="caption" className="mb-2 text-muted">
              Use @ in the composer to pin pages or databases.
            </Text>
          ) : null}

          <View className="mb-2">
            <Text variant="caption" className="text-muted">
              {discoverySummary(contextSet)} · change with the “Discover context” switch above.
            </Text>
          </View>

          <textarea
            className="chat-composer-textarea mb-2"
            placeholder="Optional instructions for this conversation…"
            rows={2}
            value={instructions ?? ''}
            onChange={(event) => onInstructionsChange(event.currentTarget.value)}
          />

          {usageLine ? <View className="mt-1">{usageLine}</View> : null}
        </View>
      ) : null}
    </View>
  );
}
